import React, { useRef, useEffect } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Text,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFeatureHighlight } from '../../hooks/useFeatureHighlight';

/**
 * FeatureHighlight Component
 * Wraps a target element and shows a "What's New" popover when the feature should be highlighted
 * 
 * @param {string} featureId - The feature identifier from featureRegistry
 * @param {React.ReactNode} children - The element to wrap and highlight
 * @param {string} title - Optional custom title (defaults to "What's New!")
 * @param {string} message - Optional custom message
 * @param {string} placement - Popover placement (defaults to "bottom")
 */
export default function FeatureHighlight({
  featureId,
  children,
  title = "What's New!",
  message,
  placement = "bottom",
}) {
  const { shouldShow, markAsSeen, loading } = useFeatureHighlight(featureId);
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = useRef(null);

  // Auto-open popover when feature should be shown
  useEffect(() => {
    if (shouldShow && !loading) {
      // Small delay to ensure the component is mounted
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    } else if (!shouldShow) {
      // Close popover if shouldShow becomes false
      setIsOpen(false);
    }
  }, [shouldShow, loading]);

  const handleDismiss = async () => {
    setIsOpen(false);
    await markAsSeen();
  };

  const handleTargetClick = async (e) => {
    // Mark as seen when user clicks the target (only if highlight is showing)
    if (shouldShow && isOpen) {
      setIsOpen(false);
      await markAsSeen();
    }
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  // Default message if none provided
  const defaultMessage = "We've added a new feature! Check it out.";

  // If loading or shouldn't show, just render children without popover
  if (loading || !shouldShow) {
    return <>{children}</>;
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleDismiss}
      placement={placement}
      closeOnBlur={false}
      initialFocusRef={triggerRef}
    >
      <PopoverTrigger>
        <Box 
          ref={triggerRef} 
          display="inline-block"
          onClick={handleTargetClick}
        >
          {children}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg={bgColor}
        borderColor={borderColor}
        maxW="300px"
      >
        <PopoverArrow bg={bgColor} borderColor={borderColor} />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold" fontSize="md">
          {title}
        </PopoverHeader>
        <PopoverBody>
          <Text fontSize="sm" color={textColor}>
            {message || defaultMessage}
          </Text>
        </PopoverBody>
        <PopoverFooter borderTop="1px" borderColor={borderColor}>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleDismiss}
            width="100%"
          >
            Got it!
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}

